import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { useSiteContent } from "@/lib/useSiteContent";
import { API } from "@/lib/content";

export default function ArticlePage() {
  const { slug } = useParams();
  const { articles } = useSiteContent();
  const [article, setArticle] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setArticle(null);
    setFailed(false);
    window.scrollTo(0, 0);
    fetch(`${API}/articles/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => setArticle(d.article))
      .catch(() => setFailed(true));
  }, [slug]);

  const meta = article || (articles || []).find((a) => a.slug === slug);

  useEffect(() => {
    if (meta?.title) document.title = `${meta.title} | Ford & Associates`;
    return () => { document.title = "Ford & Associates | Accounting, Tax & Advisory — Newmarket, Ontario"; };
  }, [meta]);

  if (failed || (!meta && !article)) {
    const stillLoading = !failed && !meta;
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center bg-[#FFFFFF] px-6 pt-[72px] text-center" data-testid="article-not-found">
        <p className="font-display text-4xl text-[#102036]">{stillLoading ? "Loading article…" : "Article not found."}</p>
        {!stillLoading && (
          <Link to="/#insights" data-testid="article-back-link" className="mt-6 rounded-full bg-[#102036] px-8 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-white">
            Back to Tax Insights
          </Link>
        )}
      </div>
    );
  }

  const paragraphs = Array.isArray(article?.body) ? article.body : Array.isArray(meta?.body) ? meta.body : [];

  return (
    <article className="bg-[#FFFFFF] pt-[72px]" data-testid="article-page">
      <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8 lg:py-24">
        <Link to="/insights" data-testid="article-back-link" className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-[#4A5568] transition-colors hover:text-[#102036]">
          <ArrowLeft className="h-4 w-4" /> All articles
        </Link>

        <div className="mt-10">
          <span className="inline-flex rounded-full border border-[#3682AE]/30 bg-[#3682AE]/10 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-[#3682AE]">
            {meta.category}
          </span>
          <h1 className="mt-6 font-display text-4xl font-medium leading-tight tracking-tight text-[#102036] sm:text-5xl">{meta.title}</h1>
          {meta.date_display && (
            <p className="mt-4 text-[12px] font-bold uppercase tracking-[0.2em] text-[#4A5568]/70" data-testid="article-date">{meta.date_display}</p>
          )}
          <p className="mt-6 text-lg leading-relaxed text-[#4A5568]">{meta.excerpt}</p>
        </div>

        <div className="mt-10 overflow-hidden rounded-lg">
          <img src={meta.image} alt={meta.title} className="aspect-[16/8] w-full object-cover" />
        </div>

        <div className="mt-12 space-y-6 border-t border-[#102036]/10 pt-10">
          {paragraphs.map((p, i) => (
            <Reveal key={i} delay={Math.min(i * 0.04, 0.3)}>
              <p className="text-[17px] leading-relaxed text-[#102036]/85">{p}</p>
            </Reveal>
          ))}
        </div>

        <div className="mt-14 rounded-lg border border-[#102036]/10 bg-[#FFFFFF] p-8 shadow-[0_8px_30px_rgb(16,32,54,0.05)] sm:p-10">
          <p className="font-display text-2xl font-semibold text-[#102036]">Need help with your situation?</p>
          <p className="mt-2 text-[15px] leading-relaxed text-[#4A5568]">
            Ford &amp; Associates provides professional tax preparation and representation for individuals and
            businesses in Newmarket and across Ontario.
          </p>
          <div className="mt-6 flex flex-wrap gap-4">
            <Link to="/#contact" data-testid="article-contact-cta" className="inline-flex items-center gap-2 rounded-full bg-[#102036] px-7 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-[#3682AE]">
              Contact us <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/client-centre" data-testid="article-client-centre-cta" className="inline-flex items-center gap-2 rounded-full border border-[#102036]/20 px-7 py-3 text-[12px] font-bold uppercase tracking-[0.14em] text-[#102036] transition-colors hover:border-[#3682AE]">
              Client Centre
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
